<template>
  <div 
    class="lead-card" 
    @click="handleCardClick"
    @touchstart.passive="onTouchStart"
    @touchmove.passive="onTouchMove"
    @touchend="onTouchEnd"
    @contextmenu.prevent="showContextMenu"
    :style="{ 
      backgroundColor: lead.cor || '#6366f1',
      backgroundImage: `linear-gradient(135deg, ${lead.cor || '#6366f1'} 0%, ${adjustColorBrightness(lead.cor || '#6366f1', -20)} 100%)`,
      color: getContrastColor(lead.cor || '#6366f1')
    }"
    :class="{ 
      'light-background': isLightColor(lead.cor || '#6366f1'), 
      'dark-background': !isLightColor(lead.cor || '#6366f1'),
      'has-unread-messages': hasUnreadMessages
    }"
  >
    <span
      v-if="hasUnreadMessages"
      class="lead-unread-msg-indicator"
      title="Nova mensagem no WhatsApp"
      aria-hidden="true"
    >
      <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
        <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H5.17L4 17.17V4h16v12z"/>
        <path d="M7 9h10v2H7zm0-3h7v2H7z"/>
      </svg>
    </span>

    <!-- Nome do Lead (sempre visível) -->
    <div class="lead-title">
      <h4>{{ lead.nome }}</h4>
      <p v-if="lead.empresa" class="lead-empresa">{{ lead.empresa }}</p>
      </div>
      
    <!-- Tags -->
    <div v-if="hasTagsToShow" class="lead-tags">
      <!-- Modo Expandido - Por Categoria -->
      <div v-if="showTagsExpanded" class="tags-expanded">
        <div 
          v-for="category in categoriesWithTags" 
          :key="category.id" 
          class="tag-category"
        >
          <span class="category-name">{{ category.nome }}:</span>
          <div class="category-tags">
            <span 
              v-for="tag in category.tags" 
              :key="tag.id" 
              class="tag-badge"
              :style="{ backgroundColor: tag.cor || '#6366f1', color: getContrastColor(tag.cor || '#6366f1') }"
            >
              <span class="tag-icon">{{ getTagIcon(tag) }}</span>
              {{ tag.nome }}
        </span>
    </div>
      </div>
    </div>
    
      <!-- Modo Minimizado - Apenas Tags (2-3 + ...) -->
      <div v-else class="tags-minimized">
        <span 
          v-for="(tag, index) in visibleTagsMinimized" 
          :key="tag.id" 
          class="tag-badge"
          :style="{ backgroundColor: tag.cor || '#6366f1', color: getContrastColor(tag.cor || '#6366f1') }"
        >
          <span class="tag-icon">{{ getTagIcon(tag) }}</span>
          {{ tag.nome }}
        </span>
        <span v-if="hasMoreTags" class="more-tags">...</span>
      </div>
    </div>
    
    <!-- Informações de Contato (condicional) -->
    <div v-if="showContactDetails && hasContactInfo" class="lead-contact">
      <div v-if="lead.email" class="contact-item">
        <svg class="contact-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"></path>
        </svg>
        <span class="contact-text">{{ lead.email }}</span>
      </div>
      
      <div
        v-if="lead.telefone"
        class="contact-item"
        :class="{ 'contact-item--locked': !canEditLead }"
        @click.stop="!canEditLead && promptLeadUpgrade($event)"
      >
        <svg class="contact-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"></path>
        </svg>
        <span v-if="canEditLead" class="contact-text">{{ lead.telefone }}</span>
        <span v-else class="contact-text contact-text--masked">
          {{ maskPhoneDisplay(lead.telefone) }}
          <svg class="contact-lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </span>
      </div>
      
      <div
        v-if="lead.celular"
        class="contact-item"
        :class="{ 'contact-item--locked': !canEditLead }"
        @click.stop="!canEditLead && promptLeadUpgrade($event)"
      >
        <svg class="contact-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"></path>
        </svg>
        <span v-if="canEditLead" class="contact-text">{{ lead.celular }}</span>
        <span v-else class="contact-text contact-text--masked">
          {{ maskPhoneDisplay(lead.celular) }}
          <svg class="contact-lock-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
        </span>
      </div>
    </div>
    
    <!-- Informações do Negócio (condicional) -->
    <div v-if="showBusinessInfo && hasBusinessInfo" class="lead-business">
      <div class="business-header">
        <svg class="business-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
        </svg>
        <span class="business-title">{{ t('kanban.leadCard.business') }}</span>
      </div>
      
      <div class="business-items">
        <div v-if="lead.valorEstimado" class="business-item">
          <span class="business-label">{{ t('kanban.leadCard.value') }}</span>
          <MoneyAmount :value="lead.valorEstimado" size="sm" class="business-value" />
    </div>
    
        <div v-if="lead.probabilidade !== null && lead.probabilidade !== undefined" class="business-item probability-item">
          <div class="probability-header">
            <span class="business-label">{{ t('kanban.leadCard.probability') }}</span>
            <span class="business-value" :class="probabilityClass">{{ lead.probabilidade }}%</span>
          </div>
          <div class="probability-progress">
            <div 
              class="progress-bar" 
              :class="probabilityProgressClass"
              :style="{ width: `${Math.min(100, Math.max(0, lead.probabilidade))}%` }"
            ></div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Usuários Atribuídos -->
    <div v-if="showUsers && hasUsersToShow" class="lead-users">
      <div class="users-container">
        <div 
          v-for="user in assignedUsers" 
          :key="user.id" 
          class="user-avatar"
          :title="userLabel(user)"
          @mouseenter="handleMouseEnter(user, $event)"
          @mouseleave="handleMouseLeave(user)"
        >
          <img 
            v-if="user.fotoPerfil" 
            :src="mediaSrc(user.fotoPerfil)" 
            :alt="userLabel(user)"
            class="user-image"
          />
          <div 
            v-else 
            class="user-initials"
            :style="{ backgroundColor: getUserColor(userLabel(user)) }"
          >
            {{ getUserInitials(userLabel(user)) }}
        </div>
    </div>
        <div v-if="hasMoreUsers" class="more-users">
          +{{ assignedUsers.length - 3 }}
        </div>
      </div>
    </div>
    
    <!-- Agendamento (se existir) -->
    <div v-if="showSchedule && isScheduled" class="lead-schedule">
      <div class="schedule-header-bar">
        <svg class="schedule-header-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
      </svg>
        <span class="schedule-header-text">{{ t('kanban.leadCard.schedule') }}</span>
    </div>
    
      <div class="schedule-datetime">
        <div class="schedule-date-time-item">
          <svg class="datetime-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
          </svg>
          <span class="datetime-value">{{ formattedScheduleDate }}</span>
  </div>
        <div class="schedule-separator">•</div>
        <div class="schedule-date-time-item">
          <svg class="datetime-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
      </svg>
          <span class="datetime-value">{{ formattedScheduleTime }}</span>
        </div>
    </div>
    
      <div v-if="motivoCompleto" class="schedule-reason-box">
        <div class="reason-card">
          <div class="reason-icon" :style="{ backgroundColor: motivoCompleto.cor }">
            <span>{{ getMotivoIcon(motivoCompleto.icone) }}</span>
          </div>
          <div class="reason-content">
            <h3>{{ motivoCompleto.nome }}</h3>
          </div>
        </div>
      </div>
    </div>
    
    <!-- Seção de Anexos e Imagens -->
    <div v-if="showAttachments && anexosStats.hasAny" class="lead-attachments">
      <div class="attachments-header">
        <svg class="attachments-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13"></path>
        </svg>
        <span class="attachments-title">{{ t('kanban.leadCard.attachments') }}</span>
      </div>
      
      <div class="attachments-items">
        <!-- Imagens -->
        <div v-if="anexosStats.imagens.hasItems" class="attachment-item">
          <svg class="attachment-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
        </svg>
          <span class="attachment-item-label">{{ t('kanban.leadCard.images') }}</span>
          <span class="attachment-item-count">{{ anexosStats.imagens.count }}</span>
    </div>
        
        <!-- Arquivos -->
        <div v-if="anexosStats.arquivos.hasItems" class="attachment-item">
          <svg class="attachment-item-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
          </svg>
          <span class="attachment-item-label">{{ t('kanban.leadCard.files') }}</span>
          <span class="attachment-item-count">{{ anexosStats.arquivos.count }}</span>
  </div>
      </div>
    </div>

    <!-- Datas de criação / atualização -->
    <div v-if="(showCreatedAt && createdAtValue) || (showUpdatedAt && updatedAtValue)" class="lead-meta-dates">
      <div class="lead-meta-dates-row">
        <div v-if="showCreatedAt && createdAtValue" class="lead-meta-date-item">
          <span class="lead-meta-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"/>
            </svg>
          </span>
          <!-- <span class="lead-meta-label">Criado:</span> -->
          <span class="lead-meta-value">{{ formatDate(createdAtValue) }}</span>
        </div>
        <div v-if="showUpdatedAt && updatedAtValue" class="lead-meta-date-item">
          <span class="lead-meta-icon">
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </span>
          <!-- <span class="lead-meta-label">Ult. Att:</span> -->
          <span class="lead-meta-value">{{ formatDate(updatedAtValue) }}</span>
        </div>
      </div>
    </div>
  </div>
  
  <!-- Floating Tooltip -->
  <Teleport to="body">
    <div 
      v-if="showTooltip && tooltipUser" 
      class="floating-tooltip"
      :style="{ left: tooltipPosition.x + 'px', top: tooltipPosition.y + 'px' }"
    >
      {{ tooltipUser.nomeCompleto }}
    </div>
  </Teleport>

  <!-- Menu de Contexto (Clique Direito) -->
  <Teleport to="body">
    <div 
      v-if="contextMenuVisible" 
      class="context-menu"
      :style="{ top: contextMenuY + 'px', left: contextMenuX + 'px' }"
      @click.stop
    >
      <div class="context-menu-item" @click="arquivarLead">
        <svg class="context-menu-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4"></path>
        </svg>
        {{ t('kanban.leadCard.archiveLead') }}
      </div>
    </div>
  </Teleport>
</template>

<script>
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { useToast, POSITION } from 'vue-toastification'
import { getTagIcon as getIconFromMapper } from '@/utils/iconMapper.js'
import { resolveMediaUrl } from '@/config/app'
import MoneyAmount from './MoneyAmount.vue'
import { useMobileCardTap } from '@/composables/useMobileCardTap.js'

export default {
  name: 'LeadCard',
  components: { MoneyAmount },
  props: {
    lead: {
      type: Object,
      required: true
    },
    showContactDetails: {
      type: Boolean,
      default: false
    },
    showBusinessInfo: {
      type: Boolean,
      default: false
    },
    showUsers: {
      type: Boolean,
      default: false
    },
    showSchedule: {
      type: Boolean,
      default: false
    },
    showTagsExpanded: {
      type: Boolean,
      default: false
    },
    showAttachments: {
      type: Boolean,
      default: false
    },
    showCreatedAt: {
      type: Boolean,
      default: false
    },
    showUpdatedAt: {
      type: Boolean,
      default: false
    },
    workspaceUsers: {
      type: Array,
      default: () => []
    },
    workspaceMotivos: {
      type: Array,
      default: () => []
    },
    workspaceTags: {
      type: Array,
      default: () => []
    },
    canEditLead: {
      type: Boolean,
      default: true
    }
  },
  emits: ['open-detail', 'archived'],
  setup(props, { emit }) {
    const toast = useToast()
    const { t, locale } = useI18n()
    const userLabel = (user) => user.nomeCompleto || t('kanban.leadCard.userFallback', { id: user.id })
    const router = useRouter()

    const dateLocale = computed(() => {
      if (locale.value === 'en') return 'en-US'
      if (locale.value === 'es') return 'es'
      return 'pt-BR'
    })

    const formatDate = (dateInput) => {
      if (!dateInput) return ''
      try {
        let d
        if (Array.isArray(dateInput)) {
          const [ano, mes, dia, hora = 0, minuto = 0, segundo = 0] = dateInput
          d = new Date(ano, mes - 1, dia, hora, minuto, segundo)
        } else {
          const raw = String(dateInput).split('.')[0]
          d = new Date(raw)
        }
        if (Number.isNaN(d.getTime())) return ''
        return d.toLocaleDateString(dateLocale.value)
      } catch (e) {
        return ''
      }
    }

    // Datas derivadas para suportar diferentes campos vindos do backend
    const createdAtValue = computed(() => {
      return props.lead.dataCriacao || props.lead.dataContato || null
    })

    const updatedAtValue = computed(() => {
      return props.lead.dataUpdate || props.lead.dataProximaAcao || null
    })
    const maskPhoneDisplay = (phone) => {
      const digits = String(phone || '').replace(/\D/g, '')
      if (!digits.length) return '...'
      return `${digits.slice(0, 3)}...`
    }

    const promptLeadUpgrade = async () => {
      const Swal = (await import('sweetalert2')).default
      const result = await Swal.fire({
        title: t('kanban.leadCard.planBlockedTitle'),
        html: `
          <p style="margin:0;line-height:1.5;">${t('kanban.leadCard.planBlockedMessage')}</p>
          <p style="margin:12px 0 0;font-size:14px;color:#64748b;">${t('kanban.leadCard.planBlockedHint')}</p>
        `,
        icon: 'info',
        showCancelButton: true,
        confirmButtonText: t('kanban.leadCard.upgradeButton'),
        cancelButtonText: t('common.close'),
        confirmButtonColor: '#2563eb',
        cancelButtonColor: '#6b7280',
        reverseButtons: true
      })
      if (result.isConfirmed) {
        await router.push('/upgrade')
      }
    }

    const openDetailModal = () => {
      if (!props.canEditLead) {
        promptLeadUpgrade()
        return
      }
      emit('open-detail', props.lead)
    }

    const {
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      onClick: handleCardClick
    } = useMobileCardTap(openDetailModal)
    
    // Verificar se tem mensagens não lidas
    const hasUnreadMessages = computed(() => {
      const count = props.lead.mensagensNaoLidas || 0
      const hasUnread = count > 0
      
      console.log(`📊 [LeadCard] Lead "${props.lead.nome}" (ID: ${props.lead.id}) - mensagensNaoLidas: ${count}, hasUnread: ${hasUnread}`)
      
      if (hasUnread) {
        console.log(`🔔 [LeadCard] BORDA DEVE APARECER para lead ID ${props.lead.id}`)
      } else {
        console.log(`✅ [LeadCard] Borda NÃO aparece para lead ID ${props.lead.id}`)
      }
      
      return hasUnread
    })

    // Menu de contexto (clique direito)
    const contextMenuVisible = ref(false)
    const contextMenuX = ref(0)
    const contextMenuY = ref(0)

    const showContextMenu = (event) => {
      event.preventDefault()
      contextMenuX.value = event.clientX
      contextMenuY.value = event.clientY
      contextMenuVisible.value = true
      
      // Fechar menu ao clicar em qualquer lugar
      const closeMenu = () => {
        contextMenuVisible.value = false
        document.removeEventListener('click', closeMenu)
      }
      document.addEventListener('click', closeMenu)
    }

    const arquivarLead = async () => {
      const Swal = (await import('sweetalert2')).default

      const result = await Swal.fire({
        title: t('kanban.leadCard.archiveTitle'),
        html: `
          <p style="margin-bottom: 12px;">${t('kanban.leadCard.archiveConfirm')}</p>
          <p style="color: #64748b; font-size: 14px;">${t('kanban.leadCard.archiveHint')}</p>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#ef4444',
        cancelButtonColor: '#6b7280',
        confirmButtonText: t('kanban.leadCard.confirmArchive'),
        cancelButtonText: t('common.cancel'),
        reverseButtons: true,
        customClass: {
          popup: 'swal-popup-custom',
          title: 'swal-title-custom',
          htmlContainer: 'swal-html-custom'
        }
      })

      if (!result.isConfirmed) {
        contextMenuVisible.value = false
        return
      }

      try {
        const api = await import('@/services/api').then(m => m.default)
        await api.put(`/leads/${props.lead.id}/arquivar`)
        
        console.log('✅ Lead arquivado com sucesso')

        toast.success(t('kanban.leadCard.archivedText'), {
          position: POSITION.BOTTOM_RIGHT,
          timeout: 3000
        })

        emit('archived', props.lead.id)
        
      } catch (error) {
        console.error('❌ Erro ao arquivar lead:', error)
        
        await Swal.fire({
          title: t('kanban.leadCard.archiveErrorTitle'),
          text: t('kanban.leadCard.archiveErrorText'),
          icon: 'error',
          confirmButtonText: 'OK',
          confirmButtonColor: '#ef4444'
        })
      } finally {
        contextMenuVisible.value = false
      }
    }

    // ========== TAGS ==========
    
    // Parse das tags do lead
    const parsedTags = computed(() => {
      try {
        if (!props.lead.tags) return []
        
        let tagsData = props.lead.tags
        
        // Se for string, fazer parse
        if (typeof tagsData === 'string') {
          tagsData = JSON.parse(tagsData)
        }
        
        // Se for array de IDs, mapear para objetos completos
        if (Array.isArray(tagsData)) {
          // Mapear IDs para tags completas do workspace
          return tagsData.map(tagId => {
            // Procurar a tag em todas as categorias
            for (const categoria of props.workspaceTags) {
              const tag = categoria.tags?.find(t => t.id === tagId)
              if (tag) {
                return tag
              }
            }
            return null
          }).filter(tag => tag !== null)
        }
        
        return []
      } catch (e) {
        console.error('Erro ao parsear tags do lead:', e)
        return []
      }
    })
    
    // Agrupar tags por categoria (mantendo a ordem das categorias)
    const categoriesWithTags = computed(() => {
      const categories = []
      
      // Iterar pelas categorias do workspace (mantém a ordem)
      for (const categoria of props.workspaceTags) {
        const tagsNaCategoria = parsedTags.value.filter(tag => {
          // Verificar se a tag pertence a esta categoria
          return categoria.tags?.some(t => t.id === tag.id)
        })
        
        if (tagsNaCategoria.length > 0) {
          categories.push({
            id: categoria.id,
            nome: categoria.nome,
            tags: tagsNaCategoria
          })
        }
      }
      
      return categories
    })
    
    // Tags visíveis no modo minimizado (primeiras 2-3)
    const visibleTagsMinimized = computed(() => {
      return parsedTags.value.slice(0, 3)
    })
    
    // Verificar se tem mais tags
    const hasMoreTags = computed(() => {
      return parsedTags.value.length > 3
    })
    
    // Verificar se tem tags para mostrar
    const hasTagsToShow = computed(() => {
      return parsedTags.value.length > 0
    })
    
    // Função para obter ícone da tag
    const getTagIcon = (tag) => {
      if (!tag.icone) return '🏷️'
      const icon = getIconFromMapper(tag.icone)
      return icon || tag.icone || '🏷️'
    }
    
    // Verificar se tem informações de contato
    const hasContactInfo = computed(() => {
      return !!(props.lead.email || props.lead.telefone || props.lead.celular || props.lead.whatsappLid)
    })
    
    // Verificar se tem informações do negócio
    const hasBusinessInfo = computed(() => {
      return !!(props.lead.valorEstimado || props.lead.probabilidade !== null && props.lead.probabilidade !== undefined)
    })
    
    // Classe de cor para probabilidade
    const probabilityClass = computed(() => {
      const prob = parseFloat(props.lead.probabilidade) || 0
      if (prob >= 80) return 'prob-high'
      if (prob >= 50) return 'prob-medium'
      return 'prob-low'
    })
    
    // Classe de cor para barra de progresso da probabilidade
    const probabilityProgressClass = computed(() => {
      const prob = parseFloat(props.lead.probabilidade) || 0
      if (prob >= 80) return 'progress-high'
      if (prob >= 50) return 'progress-medium'
      return 'progress-low'
    })
    
    // ========== USUÁRIOS ATRIBUÍDOS ==========
    
    // Parse e processamento dos usuários atribuídos
    const assignedUsers = computed(() => {
      try {
        console.log('🔍 LeadCard - lead.id:', props.lead.id)
        console.log('🔍 LeadCard - lead.usuarioAtribuidoId:', props.lead.usuarioAtribuidoId)
        console.log('🔍 LeadCard - lead.usuariosAtribuidos:', props.lead.usuariosAtribuidos)
        console.log('🔍 LeadCard - lead.assignedUsers:', props.lead.assignedUsers)
        console.log('🔍 LeadCard - workspaceUsers:', props.workspaceUsers)
        
        // PRIORIDADE 1: Verificar usuariosAtribuidos (campo do backend - pode ser string JSON ou array)
        if (props.lead.usuariosAtribuidos) {
          let userIds = []
          
          if (typeof props.lead.usuariosAtribuidos === 'string' && props.lead.usuariosAtribuidos) {
            try {
              userIds = JSON.parse(props.lead.usuariosAtribuidos)
              console.log('✅ LeadCard - Parse JSON de usuariosAtribuidos:', userIds)
            } catch (error) {
              console.error('❌ LeadCard - Erro ao fazer parse:', error)
              userIds = []
            }
          } else if (Array.isArray(props.lead.usuariosAtribuidos)) {
            userIds = props.lead.usuariosAtribuidos
            console.log('✅ LeadCard - usuariosAtribuidos já é array:', userIds)
          }
          
          if (userIds.length > 0) {
            // Mapear IDs para objetos de usuário completos
            const users = userIds.map(userId => {
              const user = props.workspaceUsers.find(u => u.id == userId)
              if (user) {
                return user
              }
              // Fallback
              return {
                id: userId,
                nomeCompleto: `Usuário ${userId}`,
                email: `usuario${userId}@exemplo.com`,
                fotoPerfil: null
              }
            })
            console.log('✅ LeadCard - Retornando', users.length, 'usuário(s)')
            return users
          }
        }
        
        // PRIORIDADE 2: Verificar assignedUsers (campo alternativo)
        if (props.lead.assignedUsers) {
          let userIds = []
          
          if (Array.isArray(props.lead.assignedUsers)) {
            userIds = props.lead.assignedUsers
          } else if (typeof props.lead.assignedUsers === 'string') {
            userIds = JSON.parse(props.lead.assignedUsers)
          }
          
          // Mapear IDs para objetos de usuário completos
          return userIds.map(userId => {
            const user = props.workspaceUsers.find(u => u.id == userId)
            if (user) {
              return user
            }
            // Fallback
            return {
              id: userId,
              nomeCompleto: `Usuário ${userId}`,
              email: `usuario${userId}@exemplo.com`,
              fotoPerfil: null
            }
          })
        }
        
        // PRIORIDADE 3: Fallback para usuarioAtribuidoId (campo legado - apenas 1 usuário)
        if (props.lead.usuarioAtribuidoId) {
          const user = props.workspaceUsers.find(u => u.id === props.lead.usuarioAtribuidoId)
          console.log('🔍 LeadCard - Usuário encontrado (legado):', user)
          
          if (user) {
            console.log('✅ LeadCard - Retornando usuário legado com foto:', user.fotoPerfil)
            return [user]
          }
          // Fallback se não encontrar
          console.log('⚠️ LeadCard - Usuário não encontrado, usando fallback')
          return [{
            id: props.lead.usuarioAtribuidoId,
            nomeCompleto: props.lead.usuarioAtribuidoNome || `Usuário ${props.lead.usuarioAtribuidoId}`,
            email: `usuario${props.lead.usuarioAtribuidoId}@exemplo.com`,
            fotoPerfil: null
          }]
        }
        
        console.log('⚠️ LeadCard - Nenhum usuário atribuído')
        return []
      } catch (e) {
        console.error('❌ Erro ao processar usuários do lead:', e)
        return []
      }
    })
    
    const hasUsersToShow = computed(() => {
      return assignedUsers.value && assignedUsers.value.length > 0
    })
    
    const hasMoreUsers = computed(() => {
      return assignedUsers.value.length > 3
    })
    
    // Função para obter iniciais do usuário
    const getUserInitials = (name) => {
      if (!name) return '??'
      const parts = name.trim().split(' ')
      if (parts.length === 1) {
        return parts[0].substring(0, 2).toUpperCase()
      }
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
    }
    
    // Função para gerar cor do avatar baseada no nome
    const getUserColor = (name) => {
      if (!name) return '#6366f1'
      
      const colors = [
        '#ef4444', '#f97316', '#f59e0b', '#eab308', '#84cc16',
        '#22c55e', '#10b981', '#14b8a6', '#06b6d4', '#0ea5e9',
        '#3b82f6', '#6366f1', '#8b5cf6', '#a855f7', '#d946ef',
        '#ec4899', '#f43f5e'
      ]
      
      let hash = 0
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash)
      }
      
      return colors[Math.abs(hash) % colors.length]
    }
    
    // Tooltip flutuante
    const showTooltip = ref(false)
    const tooltipUser = ref(null)
    const tooltipPosition = ref({ x: 0, y: 0 })
    
    const handleMouseEnter = (user, event) => {
      tooltipUser.value = user
      tooltipPosition.value = {
        x: event.clientX + 10,
        y: event.clientY + 10
      }
      showTooltip.value = true
    }
    
    const handleMouseLeave = () => {
      showTooltip.value = false
      tooltipUser.value = null
    }
    
    // ========== AGENDAMENTO ==========
    
    // Verificar se o lead tem agendamento
    const isScheduled = computed(() => {
      return !!(
        props.lead.dataAgendamento || 
        props.lead.horario || 
        props.lead.motivo
      )
    })
    
    // Formatar data do agendamento
    const formattedScheduleDate = computed(() => {
      if (!props.lead.dataAgendamento) return ''
      
      try {
        const data = props.lead.dataAgendamento
        
        // Se vier como objeto Proxy ou array, extrair o valor
        let dataStr = data
        if (typeof data === 'object' && !Array.isArray(data)) {
          dataStr = data.toString()
        }
        if (Array.isArray(data)) {
          // Formato [2025, 1, 10] -> "2025-01-10"
          const [ano, mes, dia] = data
          dataStr = `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`
        }
        
        // Formato esperado: YYYY-MM-DD
        const [ano, mes, dia] = dataStr.split('-')
        return `${dia}/${mes}/${ano}`
      } catch (error) {
        console.error('Erro ao formatar data:', error)
        return props.lead.dataAgendamento
      }
    })
    
    // Formatar hora do agendamento
    const formattedScheduleTime = computed(() => {
      if (!props.lead.horario) return ''
      return props.lead.horario
    })
    
    // Obter motivo completo do agendamento
    const motivoCompleto = computed(() => {
      if (!props.lead.motivo) return null
      
      const motivoId = props.lead.motivo
      
      console.log('🔍 LeadCard motivoCompleto - motivoId:', motivoId)
      console.log('🔍 LeadCard motivoCompleto - tipo do motivoId:', typeof motivoId)
      console.log('🔍 LeadCard motivoCompleto - workspaceMotivos:', props.workspaceMotivos)
      console.log('🔍 LeadCard motivoCompleto - workspaceMotivos length:', props.workspaceMotivos.length)
      
      // Listar todos os motivos disponíveis
      if (props.workspaceMotivos.length > 0) {
        console.log('📋 LeadCard motivoCompleto - Motivos disponíveis:')
        props.workspaceMotivos.forEach((m, idx) => {
          console.log(`  ${idx + 1}. ID: ${m.id}, Nome: ${m.motivo || m.nome}, Icone: ${m.icone}, Cor: ${m.cor}`)
        })
      }
      
      // Se for um número, buscar nos motivos configurados
      if (!isNaN(motivoId)) {
        const motivoNumerico = parseInt(motivoId)
        console.log('🔍 LeadCard motivoCompleto - Buscando por ID numérico:', motivoNumerico)
        
        const motivo = props.workspaceMotivos.find(m => m.id === motivoNumerico)
        if (motivo) {
          console.log('✅ LeadCard motivoCompleto - Motivo encontrado por ID:', motivo)
          return {
            id: motivo.id,
            nome: motivo.motivo || motivo.nome,
            icone: motivo.icone,
            cor: motivo.cor
          }
        }
      }
      
      // Se for texto livre, buscar por nome
      console.log('🔍 LeadCard motivoCompleto - Buscando por texto:', motivoId)
      const motivoPorNome = props.workspaceMotivos.find(m => 
        (m.motivo && m.motivo.toLowerCase() === motivoId.toLowerCase()) ||
        (m.nome && m.nome.toLowerCase() === motivoId.toLowerCase())
      )
      
      if (motivoPorNome) {
        console.log('✅ LeadCard motivoCompleto - Motivo encontrado por texto:', motivoPorNome)
    return {
          id: motivoPorNome.id,
          nome: motivoPorNome.motivo || motivoPorNome.nome,
          icone: motivoPorNome.icone,
          cor: motivoPorNome.cor
        }
      }
      
      // Fallback: texto livre
      console.log('⚠️ LeadCard motivoCompleto - Usando texto livre como fallback:', motivoId)
      return {
        id: null,
        nome: motivoId,
        icone: 'message',
        cor: '#6366f1'
      }
    })
    
    // Função para obter ícone do motivo
    const getMotivoIcon = (iconeName) => {
      console.log('🔍 LeadCard getMotivoIcon - iconeName recebido:', iconeName)
      
      if (!iconeName) return '📋'
      
      // Usar o iconMapper para converter o nome do ícone em emoji
      const icon = getIconFromMapper(iconeName)
      console.log('🔍 LeadCard getMotivoIcon - icon retornado:', icon)
      
      return icon || iconeName || '📋'
    }
    
    // Função para calcular cor de contraste
    const getContrastColor = (hexColor) => {
      const hex = hexColor.replace('#', '')
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      const brightness = (r * 299 + g * 587 + b * 114) / 1000
      return brightness > 128 ? '#000000' : '#FFFFFF'
    }
    
    // Função para ajustar brilho da cor
    const adjustColorBrightness = (hexColor, percent) => {
      const hex = hexColor.replace('#', '')
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      
      const newR = Math.max(0, Math.min(255, r + (r * percent / 100)))
      const newG = Math.max(0, Math.min(255, g + (g * percent / 100)))
      const newB = Math.max(0, Math.min(255, b + (b * percent / 100)))
      
      return `#${Math.round(newR).toString(16).padStart(2, '0')}${Math.round(newG).toString(16).padStart(2, '0')}${Math.round(newB).toString(16).padStart(2, '0')}`
    }
    
    // Função para detectar se a cor é clara ou escura
    const isLightColor = (hexColor) => {
      const hex = hexColor.replace('#', '')
      const r = parseInt(hex.substr(0, 2), 16)
      const g = parseInt(hex.substr(2, 2), 16)
      const b = parseInt(hex.substr(4, 2), 16)
      
      // Usar a fórmula de luminância relativa
      const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
      return luminance > 0.5
    }
    
    // ========== ANEXOS E IMAGENS ==========
    
    // Parse dos anexos do lead
    const parsedAnexos = computed(() => {
      try {
        console.log(`📎 [LeadCard] Parseando anexos do lead ${props.lead.id} (${props.lead.nome})`)
        console.log(`📎 [LeadCard] props.lead.anexos:`, props.lead.anexos)
        console.log(`📎 [LeadCard] tipo:`, typeof props.lead.anexos)
        
        if (!props.lead.anexos) {
          console.log(`⚠️ [LeadCard] Lead ${props.lead.id} sem propriedade anexos`)
          return []
        }
        
        let anexosData = props.lead.anexos
        
        // Se for string, fazer parse
        if (typeof anexosData === 'string') {
          console.log(`📎 [LeadCard] Anexos em string, fazendo parse...`)
          anexosData = JSON.parse(anexosData)
        }
        
        // Se for array, retornar
        if (Array.isArray(anexosData)) {
          console.log(`✅ [LeadCard] Lead ${props.lead.id} tem ${anexosData.length} anexo(s) parseados`)
          return anexosData
        }
        
        console.log(`⚠️ [LeadCard] Tipo de anexos não reconhecido:`, typeof anexosData)
        return []
      } catch (e) {
        console.error(`❌ [LeadCard] Erro ao parsear anexos do lead ${props.lead.id}:`, e)
        return []
      }
    })
    
    // Separar anexos por tipo (imagens vs arquivos)
    const anexosStats = computed(() => {
      console.log(`📊 [LeadCard] Calculando estatísticas de anexos para lead ${props.lead.id}`)
      console.log(`📊 [LeadCard] Total de anexos parseados:`, parsedAnexos.value.length)
      
      const imagens = parsedAnexos.value.filter(anexo => {
        // Usar tanto o campo ehImagem quanto a extensão do arquivo
        const ehImagem = anexo.ehImagem === true
        const extensao = anexo.nomeArquivo?.toLowerCase().split('.').pop()
        const extensaoImagem = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extensao)
        
        return ehImagem || extensaoImagem
      })
      
      const arquivos = parsedAnexos.value.filter(anexo => {
        // Usar tanto o campo ehImagem quanto a extensão do arquivo
        const ehImagem = anexo.ehImagem === true
        const extensao = anexo.nomeArquivo?.toLowerCase().split('.').pop()
        const extensaoImagem = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extensao)
        
        return !(ehImagem || extensaoImagem)
      })
      
      const stats = {
        imagens: {
          count: imagens.length,
          hasItems: imagens.length > 0
        },
        arquivos: {
          count: arquivos.length,
          hasItems: arquivos.length > 0
        },
        total: parsedAnexos.value.length,
        hasAny: parsedAnexos.value.length > 0
      }
      
      console.log(`📊 [LeadCard] Stats do lead ${props.lead.id}:`, stats)
      console.log(`📊 [LeadCard] showAttachments prop:`, props.showAttachments)
      console.log(`📊 [LeadCard] Anexos serão exibidos?`, props.showAttachments && stats.hasAny)
      
      return stats
    })
    
    const mediaSrc = (url) => resolveMediaUrl(url)

    return {
      t,
      userLabel,
      mediaSrc,
      canEditLead: computed(() => props.canEditLead),
      maskPhoneDisplay,
      promptLeadUpgrade,
      openDetailModal,
      handleCardClick,
      onTouchStart,
      onTouchMove,
      onTouchEnd,
      hasUnreadMessages,
      contextMenuVisible,
      contextMenuX,
      contextMenuY,
      showContextMenu,
      arquivarLead,
      categoriesWithTags,
      visibleTagsMinimized,
      hasMoreTags,
      hasTagsToShow,
      getTagIcon,
      hasContactInfo,
      hasBusinessInfo,
      probabilityClass,
      probabilityProgressClass,
      assignedUsers,
      hasUsersToShow,
      hasMoreUsers,
      getUserInitials,
      getUserColor,
      adjustColorBrightness,
      isLightColor,
      handleMouseEnter,
      handleMouseLeave,
      showTooltip,
      tooltipUser,
      tooltipPosition,
      isScheduled,
      formattedScheduleDate,
      formattedScheduleTime,
      motivoCompleto,
      getMotivoIcon,
      getContrastColor,
      parsedAnexos,
      anexosStats,
      // Datas
      formatDate,
      createdAtValue,
      updatedAtValue
    }
  }
}
</script>

<style scoped>
/* Card principal com tema */
.lead-card {
  background-color: var(--bg-primary);
  border: 1px solid var(--border-primary);
  border-radius: 4px;
  padding: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;
}

/* Animação RGB para mensagens não lidas */
.lead-card.has-unread-messages {
  border-left: 3px solid;
  border-left-color: #ff0000;
  animation: rgb-border-cycle 3s linear infinite;
}

@keyframes rgb-border-cycle {
  0% { border-left-color: #ff0000; } /* Vermelho */
  16.66% { border-left-color: #ff8800; } /* Laranja */
  33.33% { border-left-color: #ffff00; } /* Amarelo */
  50% { border-left-color: #00ff00; } /* Verde */
  66.66% { border-left-color: #00ffff; } /* Ciano */
  83.33% { border-left-color: #0000ff; } /* Azul */
  100% { border-left-color: #ff0000; } /* Volta ao vermelho */
}

.lead-unread-msg-indicator {
  position: absolute;
  top: 4px;
  right: 4px;
  z-index: 3;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  color: #dc2626;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  pointer-events: none;
}

.lead-unread-msg-indicator svg {
  width: 11px;
  height: 11px;
}

.dark-background .lead-unread-msg-indicator {
  background: rgba(255, 255, 255, 0.92);
  color: #ef4444;
}

.light-background .lead-unread-msg-indicator {
  background: rgba(0, 0, 0, 0.72);
  color: #fff;
}

.lead-card.has-unread-messages .lead-title h4 {
  padding-right: 22px;
}

/* Títulos adaptam ao fundo do card */
.lead-title h4 {
  color: rgba(0, 0, 0, 0.9);
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.5);
  font-weight: 700;
  font-size: 15px;
}

.dark-background .lead-title h4 {
  color: rgba(255, 255, 255, 0.95);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.7);
}

.lead-title p,
.lead-empresa {
  color: rgba(0, 0, 0, 0.75);
  text-shadow: 0 1px 2px rgba(255, 255, 255, 0.4);
  font-weight: 500;
}

.dark-background .lead-title p,
.dark-background .lead-empresa {
  color: rgba(255, 255, 255, 0.85);
  text-shadow: 0 1px 3px rgba(0, 0, 0, 0.6);
}

.lead-card:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.25);
  transform: translateY(-2px);
  filter: brightness(1.05);
}

/* Título do Lead */
.lead-title {
  margin-bottom: 0;
  position: relative;
  z-index: 1;
}

.lead-title h4 {
  margin: 0;
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
}

.lead-empresa {
  font-size: 11px;
  margin-top: 2px;
  font-weight: 500;
}

/* ========== TAGS ========== */

.lead-tags {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.15);
  position: relative;
  z-index: 1;
}

.dark-background .lead-tags {
  border-top-color: rgba(255, 255, 255, 0.2);
}

/* Modo Expandido */
.tags-expanded {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.tag-category {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.category-name {
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.dark-background .category-name {
  color: rgba(255, 255, 255, 0.75);
}

.category-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
}

/* Modo Minimizado */
.tags-minimized {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  align-items: center;
}

/* Badge da Tag */
.tag-badge {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  font-size: 10px;
  font-weight: 500;
  border-radius: 4px;
  white-space: nowrap;
  transition: all 0.15s ease;
}

/* Ícone da Tag */
.tag-icon {
  font-size: 14px !important;
  line-height: 1 !important;
  display: inline-block !important;
  margin-right: 6px !important;
  margin-left: -2px !important;
  color: inherit !important;
  opacity: 1 !important;
  visibility: visible !important;
  width: auto !important;
  height: auto !important;
  min-width: 14px !important;
  min-height: 14px !important;
}

.tag-badge:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Indicador "..." */
.more-tags {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.6);
  padding: 0 4px;
}

.dark-background .more-tags {
  color: rgba(255, 255, 255, 0.7);
}

/* Datas de criação / atualização (rodapé) */
.lead-meta-dates {
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px dashed rgba(0, 0, 0, 0.15);
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 10px;
  color: rgba(0, 0, 0, 0.65);
}

.dark-background .lead-meta-dates {
  border-top-color: rgba(255, 255, 255, 0.25);
  color: rgba(255, 255, 255, 0.85);
}

.lead-meta-dates-row {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  align-items: center;
}

.lead-meta-date-item {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  opacity: 0.9;
}

.lead-meta-label {
  font-weight: 500;
}

.lead-meta-value {
  font-weight: 400;
}

.lead-meta-separator {
  margin-right: 2px;
}

.lead-meta-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 14px;
  height: 14px;
}

.lead-meta-icon svg {
  width: 100%;
  height: 100%;
  stroke: currentColor;
  fill: none;
}

/* Informações de Contato */
.lead-contact {
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px solid rgba(0, 0, 0, 0.15);
}

.dark-background .lead-contact {
  border-top-color: rgba(255, 255, 255, 0.2);
}

.contact-item {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
}

.contact-item:first-child {
  margin-top: 0;
}

.contact-icon {
  width: 14px;
  height: 14px;
  color: rgba(0, 0, 0, 0.6);
  flex-shrink: 0;
}

.dark-background .contact-icon {
  color: rgba(255, 255, 255, 0.7);
}

.contact-text {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.7);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark-background .contact-text {
  color: rgba(255, 255, 255, 0.8);
}

.contact-item--locked {
  cursor: pointer;
}

.contact-item--locked:hover {
  opacity: 0.92;
}

.contact-text--masked {
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.contact-lock-icon {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
  opacity: 0.9;
}

/* Informações do Negócio */
.lead-business {
  margin-top: 6px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  transition: all 0.2s ease;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

/* Adapta para fundos claros */
.light-background .lead-business {
  background: rgba(0, 0, 0, 0.04);
  border-color: rgba(0, 0, 0, 0.08);
}

/* Adapta para fundos escuros */
.dark-background .lead-business {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
}

.business-header {
  display: flex;
  align-items: center;
  gap: 5px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 5px;
}

.dark-background .business-header {
  border-bottom-color: rgba(255, 255, 255, 0.15);
}

.business-icon {
  width: 12px;
  height: 12px;
  color: #10b981;
  flex-shrink: 0;
}

.business-title {
  font-size: 9px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.dark-background .business-title {
  color: rgba(255, 255, 255, 0.85);
}

.business-items {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.business-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.03);
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.06);
  transition: all 0.2s ease;
  backdrop-filter: blur(8px);
  box-shadow: none;
  margin-bottom: 0;
}

.dark-background .business-item {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.15);
}

.business-item:hover {
  background: rgba(0, 0, 0, 0.06);
  border-color: rgba(0, 0, 0, 0.1);
  transform: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.dark-background .business-item:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(255, 255, 255, 0.2);
}

.business-label {
  font-size: 9px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.65);
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

.dark-background .business-label {
  color: rgba(255, 255, 255, 0.75);
}

.business-value {
  font-size: 11px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.85);
}

.dark-background .business-value {
  color: rgba(255, 255, 255, 0.95);
}

/* Classes de probabilidade */
.prob-high {
  color: #10b981;
}

.prob-medium {
  color: #f59e0b;
}

.prob-low {
  color: #ef4444;
}

/* Barra de progresso da probabilidade */
.probability-item {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.probability-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.probability-progress {
  width: 100%;
  height: 6px;
  background: var(--bg-primary);
  border-radius: 4px;
  overflow: hidden;
  position: relative;
  border: 1px solid var(--border-primary);
}

.progress-bar {
  height: 100%;
  border-radius: 4px;
  transition: width 0.3s ease, background-color 0.3s ease;
  position: relative;
}

.progress-bar::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 50%, transparent 100%);
  animation: shimmer 2s infinite;
}

@keyframes shimmer {
  0% { transform: translateX(-100%); }
  100% { transform: translateX(100%); }
}

.progress-high {
  background: linear-gradient(90deg, #10b981 0%, #059669 100%);
  box-shadow: 0 0 8px rgba(16, 185, 129, 0.3);
}

.progress-medium {
  background: linear-gradient(90deg, #f59e0b 0%, #d97706 100%);
  box-shadow: 0 0 8px rgba(245, 158, 11, 0.3);
}

.progress-low {
  background: linear-gradient(90deg, #ef4444 0%, #dc2626 100%);
  box-shadow: 0 0 8px rgba(239, 68, 68, 0.3);
}

/* Removido - agora usa .light-background e .dark-background */

/* ========== USUÁRIOS ATRIBUÍDOS ========== */

.lead-users {
  margin-top: 6px;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.04);
  border-radius: 4px;
  border: 1px solid rgba(0, 0, 0, 0.08);
  backdrop-filter: blur(10px);
  position: relative;
  z-index: 1;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.dark-background .lead-users {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
}

.users-container {
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.user-avatar {
  position: relative;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(0, 0, 0, 0.15);
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s ease;
  cursor: pointer;
  flex-shrink: 0;
}

.dark-background .user-avatar {
  border-color: rgba(255, 255, 255, 0.3);
}

.user-avatar:hover {
  transform: scale(1.1);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  z-index: 10;
}

.user-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.user-initials {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 700;
  color: white;
  text-transform: uppercase;
}

.more-users {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: var(--bg-tertiary);
  border: 2px solid var(--border-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 9px;
  font-weight: 700;
  color: var(--text-secondary);
}

/* Floating Tooltip */
.floating-tooltip {
  position: fixed;
  background: var(--bg-modal);
  color: var(--text-primary);
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  z-index: 99999;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  border: 1px solid var(--border-primary);
}

.tema_clara .floating-tooltip {
  background: rgba(255, 255, 255, 0.98);
  color: #1f2937;
  border-color: rgba(229, 231, 235, 0.8);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.tema_escura .floating-tooltip {
  background: rgba(31, 41, 55, 0.98);
  color: #f9fafb;
  border-color: rgba(75, 85, 99, 0.6);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

/* ========== AGENDAMENTO ========== */

/* Agendamento - Design Elegante e Compacto */
.lead-schedule {
  margin-top: 6px;
  padding: 0;
  border-radius: 4px;
  overflow: hidden;
  border: 1px solid rgba(0, 0, 0, 0.08);
  background: rgba(0, 0, 0, 0.04);
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.dark-background .lead-schedule {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
}

/* Header com ícone */
.schedule-header-bar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.03);
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

.dark-background .schedule-header-bar {
  background: rgba(255, 255, 255, 0.1);
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

.schedule-header-icon {
  width: 12px;
  height: 12px;
  color: #6366f1;
  flex-shrink: 0;
}

.schedule-header-text {
  font-size: 9px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.dark-background .schedule-header-text {
  color: rgba(255, 255, 255, 0.85);
}

/* Data e Hora em linha */
.schedule-datetime {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background-color: transparent;
}

.schedule-date-time-item {
  display: flex;
  align-items: center;
  gap: 4px;
}

.datetime-icon {
  width: 11px;
  height: 11px;
  color: #6366f1;
  flex-shrink: 0;
}

.datetime-value {
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
}

.dark-background .datetime-value {
  color: rgba(255, 255, 255, 0.85);
}

.schedule-separator {
  color: rgba(0, 0, 0, 0.4);
  font-size: 10px;
  font-weight: bold;
}

.dark-background .schedule-separator {
  color: rgba(255, 255, 255, 0.5);
}

/* Box do motivo */
.schedule-reason-box {
  display: flex;
  align-items: center;
  padding: 6px 8px;
  background-color: transparent;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
}

.dark-background .schedule-reason-box {
  border-top-color: rgba(255, 255, 255, 0.15);
}

.reason-card {
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  padding: 5px 8px;
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.dark-background .reason-card {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.15);
}

.reason-card:hover {
  border-color: rgba(0, 0, 0, 0.2);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.dark-background .reason-card:hover {
  border-color: rgba(255, 255, 255, 0.35);
}

.reason-icon {
  width: 20px;
  height: 20px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 11px;
  flex-shrink: 0;
}

.reason-content {
  flex: 1;
}

.reason-content h3 {
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
  margin: 0;
  line-height: 1.3;
}

.dark-background .reason-content h3 {
  color: rgba(255, 255, 255, 0.85);
}

/* Removido - agora usa .light-background e .dark-background */

/* ========== ANEXOS E IMAGENS ========== */

.lead-attachments {
  margin-top: 6px;
  padding: 8px;
  background: rgba(0, 0, 0, 0.04);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 4px;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
}

.dark-background .lead-attachments {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
}

.attachments-header {
  display: flex;
  align-items: center;
  gap: 5px;
  padding-bottom: 4px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  margin-bottom: 5px;
}

.dark-background .attachments-header {
  border-bottom-color: rgba(255, 255, 255, 0.15);
}

.attachments-icon {
  width: 12px;
  height: 12px;
  color: #f59e0b;
  flex-shrink: 0;
}

.attachments-title {
  font-size: 9px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.7);
  text-transform: uppercase;
  letter-spacing: 0.8px;
}

.dark-background .attachments-title {
  color: rgba(255, 255, 255, 0.85);
}

.attachments-items {
  display: flex;
  flex-direction: column;
  gap: 3px;
}

.attachment-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  transition: all 0.2s ease;
  box-shadow: none;
}

.dark-background .attachment-item {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.15);
}

.attachment-item:hover {
  border-color: #f59e0b;
  background: rgba(0, 0, 0, 0.06);
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
}

.dark-background .attachment-item:hover {
  background: rgba(255, 255, 255, 0.15);
}

.attachment-item-icon {
  width: 14px;
  height: 14px;
  color: #f59e0b;
  flex-shrink: 0;
}

.attachment-item-label {
  font-size: 10px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.75);
  flex: 1;
}

.dark-background .attachment-item-label {
  color: rgba(255, 255, 255, 0.85);
}

.attachment-item-count {
  font-size: 10px;
  font-weight: 700;
  color: rgba(0, 0, 0, 0.75);
  background: rgba(0, 0, 0, 0.06);
  padding: 2px 6px;
  border-radius: 4px;
  min-width: 16px;
  text-align: center;
}

.dark-background .attachment-item-count {
  color: rgba(255, 255, 255, 0.9);
  background: rgba(255, 255, 255, 0.15);
}

/* Removido - agora usa .light-background e .dark-background */

/* ✨ TRANSIÇÕES SUAVES PARA ATUALIZAÇÕES EM TEMPO REAL ✨ */
.lead-card {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.lead-card * {
  transition: opacity 0.2s ease, color 0.2s ease, background-color 0.2s ease, transform 0.2s ease;
}

/* Animação suave para mudanças de conteúdo */
.lead-title h4,
.lead-contact-info,
.lead-business-info,
.lead-schedule,
.lead-attachments {
  transition: all 0.25s ease-in-out;
}

/* Impedir piscar ao atualizar dados */
.lead-card-footer,
.lead-meta,
.lead-users {
  will-change: contents;
  transition: opacity 0.2s ease;
}

/* Suavizar mudanças de cor */
.tag-badge,
.user-avatar,
.origin-badge,
.schedule-icon {
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Menu de Contexto */
.context-menu {
  position: fixed;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 4px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15);
  z-index: 9999;
  min-width: 180px;
  padding: 4px 0;
}

.dark .context-menu {
  background: #1f2937;
  border-color: #374151;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  cursor: pointer;
  font-size: 14px;
  color: #374151;
  transition: all 0.2s;
}

.dark .context-menu-item {
  color: #e5e7eb;
}

.context-menu-item:hover {
  background: #f3f4f6;
  color: #1f2937;
}

.dark .context-menu-item:hover {
  background: #374151;
  color: #fff;
}

.context-menu-icon {
  width: 18px;
  height: 18px;
}

/* Customização do SweetAlert2 */
:deep(.swal-popup-custom) {
  border-radius: 4px;
  padding: 24px;
}

:deep(.swal-title-custom) {
  font-size: 24px;
  font-weight: 600;
  color: #1f2937;
}

:deep(.swal-html-custom) {
  font-size: 15px;
  line-height: 1.6;
}

:deep(.swal-html-custom p) {
  margin: 0;
}

/* Dark mode para SweetAlert2 */
.dark :deep(.swal2-popup) {
  background: #1f2937 !important;
  color: #e5e7eb !important;
}

.dark :deep(.swal2-title) {
  color: #f3f4f6 !important;
}

.dark :deep(.swal2-html-container) {
  color: #d1d5db !important;
}

.dark :deep(.swal2-icon.swal2-warning) {
  border-color: #f59e0b !important;
  color: #f59e0b !important;
}

.dark :deep(.swal2-icon.swal2-success) {
  border-color: #10b981 !important;
}

.dark :deep(.swal2-icon.swal2-success [class^='swal2-success-line']) {
  background-color: #10b981 !important;
}

.dark :deep(.swal2-icon.swal2-success .swal2-success-ring) {
  border-color: rgba(16, 185, 129, 0.3) !important;
}

.dark :deep(.swal2-icon.swal2-error) {
  border-color: #ef4444 !important;
  color: #ef4444 !important;
}
</style>
